import { useCallback } from "react";
import { usePaste } from "@thenounproject/lingo-core";

import { validatePastedColors, convertTinyColorToColorData } from "@helpers/colorPaste";
import { validateFigmaLink } from "@helpers/figma";
import { validateSvgText } from "@helpers/svgPaste";

import type { UsePasteDetectionOptions, DetectedPasteData } from "../types";

/**
 * Low-level hook for detecting and validating pasted content.
 * Detects paste and returns validated data.
 *
 * Use this when you want to handle paste data yourself (e.g., file pickers, replacements).
 * For automatic asset creation, use useAssetPaste instead.
 *
 * @example
 * const { pasteHandler } = usePasteDetection({
 *   features: { colors: false, figma: false }, // Only detect images/SVG
 *   onDetected: (data) => {
 *     if (data.type === 'image' || data.type === 'svg') {
 *       onPickFiles([data.file]);
 *     }
 *   },
 * });
 * usePaste(pasteHandler, shouldDisable);
 */
export function usePasteDetection(options: UsePasteDetectionOptions) {
  const { features = {}, onDetected } = options;

  const enableColors = features.colors !== false,
    enableFigma = features.figma !== false,
    enableImages = features.images !== false,
    enableSvg = features.svg !== false;

  /**
   * Main paste handler compatible with usePaste hook.
   * Detects and validates paste data, then calls onDetected callback.
   */
  const pasteHandler: Parameters<typeof usePaste>[0] = useCallback(
    async ({ event, getText, getData }) => {
      const textData = getText();

      // Try color paste
      if (enableColors) {
        const validatedColors = validatePastedColors(textData);
        if (validatedColors) {
          event.preventDefault();
          const colorsData = validatedColors.map(convertTinyColorToColorData);
          await onDetected({
            type: "color",
            colors: colorsData,
          } as DetectedPasteData);
          return;
        }
      }

      // Try Figma paste
      if (enableFigma) {
        if (validateFigmaLink(textData)) {
          event.preventDefault();
          await onDetected({
            type: "figma",
            url: textData,
          } as DetectedPasteData);
          return;
        }
      }

      // Try SVG text paste
      if (enableSvg) {
        const validatedSvg = validateSvgText(textData);
        if (validatedSvg) {
          event.preventDefault();
          // Convert SVG string to File
          const blob = new Blob([validatedSvg], { type: "image/svg+xml" });
          const file = new File([blob], "pasted-image.svg", { type: "image/svg+xml" });
          await onDetected({
            type: "svg",
            file,
          } as DetectedPasteData);
          return;
        }
      }

      // Try SVG blob paste
      if (enableSvg) {
        const svgData = getData("image/svg+xml");
        if (svgData) {
          event.preventDefault();
          await onDetected({
            type: "svg",
            file: svgData,
          } as DetectedPasteData);
          return;
        }
      }

      // Try image paste
      if (enableImages) {
        const imageData =
          getData("image/png") ||
          getData("image/jpeg") ||
          getData("image/gif") ||
          getData("image/webp") ||
          getData("image/tiff");
        if (imageData) {
          event.preventDefault();
          await onDetected({
            type: "image",
            file: imageData,
          } as DetectedPasteData);
          return;
        }
      }

      const pdfData = getData("application/pdf");
      if (pdfData) {
        event.preventDefault();
        await onDetected({
          type: "pdf",
          file: pdfData,
        } as DetectedPasteData);
        return;
      }

      // No valid paste data detected - do nothing
    },
    [enableColors, enableFigma, enableImages, enableSvg, onDetected]
  );

  return {
    /** Paste handler for use with usePaste hook from @thenounproject/lingo-core */
    pasteHandler,
  };
}
